import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type StandingsResponse } from "@/lib/api";
import { valuePortfolio } from "@/lib/portfolio";
import { useQuotes, useSecurities } from "./useQuotes";

/**
 * The standings, and one member's book behind them.
 *
 * Slower than the quote poll on purpose. The Worker rebuilds the ranking once
 * every twenty seconds and serves the same payload to everyone in between, so
 * asking three times a minute would return the identical bytes twice. Thirty
 * seconds keeps the screen alive without the club generating a request storm
 * on the one endpoint that reads every portfolio.
 */

const STANDINGS_REFRESH_MS = 30_000;

export const STANDINGS_KEY = ["standings"] as const;

export interface StandingsState {
  standings: StandingsResponse | null;
  /** The signed-in member's own row, for the "you" strip. */
  mine: StandingsResponse["rows"][number] | null;
  note: string | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isFetching: boolean;
  refetch: () => Promise<unknown>;
}

export function useStandings(userId: string | undefined): StandingsState {
  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: STANDINGS_KEY,
    queryFn: api.standings,
    refetchInterval: STANDINGS_REFRESH_MS,
    staleTime: STANDINGS_REFRESH_MS,
    // Keep the last standings on screen while the next poll is in flight, so
    // the table never blanks and re-sorts under the reader's eyes.
    placeholderData: (previous) => previous,
    retry: 1,
  });

  // `?? []` and not `data?.rows.find(...)`. This screen is the one read whose
  // payload is assembled from five upstreams and memoised across the club, so
  // a partial body reaches everybody at once — and a `.find` on a missing
  // `rows` throws during render, which without a boundary blanked the whole
  // terminal rather than this panel.
  const mine = useMemo(
    () => (data?.rows ?? []).find((row) => row.userId === userId) ?? null,
    [data, userId],
  );

  return {
    standings: data ?? null,
    mine,
    note: data?.note ?? null,
    isLoading: isPending,
    isError,
    error,
    isFetching,
    refetch,
  };
}

/**
 * Another member's positions and fills, valued in the browser.
 *
 * The same `valuePortfolio()` that draws the member's own grid, against the
 * same quote cache — which is the point of the endpoint being unpriced. Two
 * screens showing the same position at two different prices, because one was
 * valued on the server and one in the browser, is the failure this avoids.
 */
export function useMemberBook(portfolioId: string | null) {
  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: ["member-book", portfolioId],
    queryFn: () => api.memberBook(portfolioId!),
    enabled: Boolean(portfolioId),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const positions = data?.positions ?? [];
  const symbols = useMemo(() => positions.map((p) => p.symbol), [positions]);

  const { quotes } = useQuotes(symbols, Boolean(portfolioId));
  const { securities } = useSecurities(symbols, Boolean(portfolioId));

  const { rows, totals } = useMemo(
    () =>
      valuePortfolio({
        positions,
        quotes,
        securities,
        cash: data?.cash ?? 0,
        startingCash: data?.startingCash ?? 0,
      }),
    [positions, quotes, securities, data?.cash, data?.startingCash],
  );

  return {
    displayName: data?.member.displayName ?? null,
    role: data?.member.role ?? "member",
    rows,
    totals,
    trades: data?.trades ?? [],
    hasData: Boolean(data),
    isLoading: isPending && Boolean(portfolioId),
    isError,
    error,
    isFetching,
    refetch,
  };
}
